// ===========================================
// Hybrid Booking Toggle Banner
// ===========================================
// Admin toggle to open/close hybrid individual bookings
// for all hybrid lessons in the current term

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Switch,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Skeleton,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Popover,
  Divider,
  Alert,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from '@mui/material';
import {
  EventAvailable as BookingOpenIcon,
  EventBusy as BookingClosedIcon,
  Lock as PreBookIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  EventBusy as CancelBookingIcon,
  InfoOutlined as InfoOutlinedIcon,
  Send as NudgeIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import ChangeClinicTeacherDialog from '../admin/hybridBooking/ChangeClinicTeacherDialog';
import { format } from 'date-fns';
import { useMutation } from '@tanstack/react-query';
import { useHybridBookingStatus, useBulkToggleBookings, useBulkToggleOverflow, useTermBookingOpenStats, useAdminAvailableSlots, useAdminRescheduleBooking, useAdminCancelBooking, useSendReminders, nudgeableClinicWeeks } from '../../hooks/useHybridBooking';
import { useTenant } from '../../contexts/TenantContext';
import { BookingOpenStatsRecipient, BookingOpenStatsWeekEntry, TermBookingOpenStatsLesson } from '../../api/hybridBooking.api';
import { messagesApi } from '../../api/messages.api';
import SlotPicker from '../booking/SlotPicker';
import { parseUTCDate, formatUTCDate } from '../../utils/dateUtils';
import { getContrastTextColor } from '../../styles/brandColors';

type FilterTab = 'all' | 'booked' | 'partial' | 'pending';

export default function HybridBookingToggle() {
  const { data: status, isLoading } = useHybridBookingStatus();
  const bulkToggle = useBulkToggleBookings();
  const overflowToggle = useBulkToggleOverflow();
  const navigate = useNavigate();
  const { schoolSlug } = useTenant();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<boolean>(false);
  const [statsModalOpen, setStatsModalOpen] = useState(false);
  const [filterTab, setFilterTab] = useState<FilterTab>('all');
  const [filterLessonId, setFilterLessonId] = useState<string>('all');
  const [contactRow, setContactRow] = useState<(BookingOpenStatsRecipient & { lessonName: string; lessonId: string }) | null>(null);
  const [contactAnchor, setContactAnchor] = useState<HTMLElement | null>(null);
  const [messageText, setMessageText] = useState('');
  const [messageSent, setMessageSent] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<{ entry: BookingOpenStatsWeekEntry; row: RecipientRow } | null>(null);

  const queryClient = useQueryClient();

  const handleRowClick = (row: BookingOpenStatsRecipient & { lessonName: string; lessonId: string }, anchor: HTMLElement) => {
    setContactRow(row);
    setContactAnchor(anchor);
  };

  const handleContactClose = () => {
    setContactRow(null);
    setContactAnchor(null);
    setMessageText('');
    setMessageSent(false);
  };

  const sendMessage = useMutation({
    mutationFn: (content: string) =>
      messagesApi.sendMessage({ recipientId: contactRow!.parentUserId, content }),
    onSuccess: () => {
      setMessageSent(true);
      setMessageText('');
    },
  });

  const handleBookingMutated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['hybridBooking', 'termBookingOpenStats'] });
  }, [queryClient]);

  const hasHybridLessons = (status?.totalHybridLessons ?? 0) > 0;
  const isOpen = status?.allOpen ?? false;
  const isDisabled = !hasHybridLessons;
  // Overflow is a dependent extension of an open booking window: the switch is
  // only enabled while bookings are open. Mirrors how isOpen uses allOpen.
  const isOverflowOpen = status?.overflowAllOpen ?? false;

  // Only fetch stats when bookings are open
  const { data: termStats, isLoading: statsLoading } = useTermBookingOpenStats(isOpen && hasHybridLessons);

  const handleToggleClick = (newValue: boolean) => {
    if (isDisabled) return;
    setPendingAction(newValue);
    setConfirmOpen(true);
  };

  const handleOverflowToggle = (newValue: boolean) => {
    // Only meaningful while bookings are open; no confirm dialog (reversible + gated).
    if (!isOpen) return;
    overflowToggle.mutate(newValue);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    bulkToggle.mutate(pendingAction);
  };

  const handleCancel = () => {
    setConfirmOpen(false);
  };

  // Flatten recipients across all lessons for display, enriched with per-lesson week counts
  // Sprint B: admin "nudge now" for one class + clinic week. The hook owns the
  // snackbar wording and the timeout handling; this just tracks which column is
  // busy for the most recent press.
  const { mutate: nudge } = useSendReminders();
  const [nudgingKey, setNudgingKey] = useState<string | null>(null);
  // `mutate` is referentially stable in React Query v5; the result object is
  // not, so depending on the whole object would rebuild this every render.
  const handleNudgeClick = useCallback(
    (row: RecipientRow, weekNumber: number) => {
      const key = `${row.lessonId}#${weekNumber}`;
      setNudgingKey(key);
      nudge(
        { lessonId: row.lessonId, weekNumber },
        // Functional update: a slow press settling after a newer one must not
        // clear the newer chip.
        { onSettled: () => setNudgingKey((k) => (k === key ? null : k)) }
      );
    },
    [nudge]
  );

  const allRecipients = useMemo<Array<BookingOpenStatsRecipient & { lessonName: string; lessonId: string; totalWeeks: number; bookedWeeks: number; allWeeksNotApplicable: boolean }>>(() => {
    if (!termStats) return [];
    return termStats.lessons.flatMap((lesson) =>
      lesson.recipients.map((r) => ({
        ...r,
        lessonName: lesson.lessonName,
        lessonId: lesson.lessonId,
        // Only the clinic weeks this student has STARTED by count toward their total.
        totalWeeks: applicableClinicWeeks(lesson.individualWeeks, r.startWeek).length,
        // ...and only bookings FOR those weeks count as booked: a booking left in a week
        // before the start (start week moved later) still shows in the grid, but never
        // makes a student "fully booked".
        bookedWeeks: applicableBookingCount(r.weekBookings, r.startWeek),
        allWeeksNotApplicable:
          lesson.individualWeeks.length > 0 && applicableClinicWeeks(lesson.individualWeeks, r.startWeek).length === 0,
      }))
    );
  }, [termStats]);

  const filteredRecipients = useMemo(() => {
    let result = filterLessonId === 'all' ? allRecipients : allRecipients.filter((r) => r.lessonId === filterLessonId);
    if (filterTab === 'booked') result = result.filter((r) => r.bookedWeeks >= r.totalWeeks && r.totalWeeks > 0);
    else if (filterTab === 'partial') result = result.filter((r) => r.bookedWeeks > 0 && r.bookedWeeks < r.totalWeeks);
    else if (filterTab === 'pending') result = result.filter(isPendingRecipient);
    return result;
  }, [allRecipients, filterTab, filterLessonId]);

  const multipleLessons = (termStats?.lessons.length ?? 0) > 1;

  const bookingChipStats = useMemo(() => {
    if (!termStats) return null;
    const studentWeeks: Record<string, { total: number; booked: number }> = {};
    for (const lesson of termStats.lessons) {
      for (const recipient of lesson.recipients) {
        const totalWeeks = applicableClinicWeeks(lesson.individualWeeks, recipient.startWeek).length;
        const key = recipient.studentId;
        if (!studentWeeks[key]) studentWeeks[key] = { total: 0, booked: 0 };
        studentWeeks[key].total += totalWeeks;
        studentWeeks[key].booked += applicableBookingCount(recipient.weekBookings, recipient.startWeek);
      }
    }
    let fullyBooked = 0;
    let partiallyBooked = 0;
    for (const { total, booked } of Object.values(studentWeeks)) {
      if (booked >= total && total > 0) fullyBooked++;
      else if (booked > 0) partiallyBooked++;
    }
    return { fullyBooked, partiallyBooked, notificationsSent: termStats.totalEmailsSent };
  }, [termStats]);

  if (isLoading) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={24} />
              <Skeleton variant="text" width="40%" height={18} />
            </Box>
            <Skeleton variant="rectangular" width={50} height={28} sx={{ borderRadius: 7 }} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  const getSubtitleText = () => {
    if (!hasHybridLessons) {
      return status?.termName
        ? `${status.termName} • No hybrid lessons`
        : 'No hybrid lessons in current term';
    }
    const termLabel = status?.termName ?? 'Current term';
    const lessonCount = `${status?.totalHybridLessons} hybrid lesson${status?.totalHybridLessons !== 1 ? 's' : ''}`;
    const partialOpen =
      isOpen && status && status.openCount < status.totalHybridLessons
        ? ` (${status.openCount}/${status.totalHybridLessons} open)`
        : '';
    return `${termLabel} • ${lessonCount}${partialOpen}`;
  };

  return (
    <>
      <Card
        sx={{
          mb: 2,
          border: '1px solid',
          borderColor: isOpen ? '#96DAC9' : '#e0e0e0',
          bgcolor: isOpen ? 'rgba(150, 218, 201, 0.08)' : 'background.paper',
          opacity: isDisabled ? 0.7 : 1,
          transition: 'all 0.3s ease',
        }}
      >
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          {/* Title row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.25 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isOpen ? 'rgba(150, 218, 201, 0.2)' : 'rgba(0, 0, 0, 0.06)',
                color: isOpen ? '#5cb399' : 'text.secondary',
                transition: 'all 0.3s ease',
              }}
            >
              {isOpen ? <BookingOpenIcon fontSize="small" /> : <BookingClosedIcon fontSize="small" />}
            </Box>
            <Typography variant="subtitle2" fontWeight={600}>
              Hybrid Individual Bookings
            </Typography>
          </Box>

          {/* Subtitle row */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, pl: '51px' }}>
            {getSubtitleText()}
          </Typography>

          {/* Controls row: chip + toggle + Pre-Book button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: '51px' }}>
            <Chip
              label={isDisabled ? 'N/A' : isOpen ? 'Open' : 'Closed'}
              size="small"
              sx={{
                bgcolor: isOpen ? '#c5ebe2' : '#f5f5f5',
                color: isOpen ? '#5cb399' : 'text.secondary',
                fontWeight: 600,
                fontSize: '0.7rem',
              }}
            />
            {bulkToggle.isPending ? (
              <CircularProgress size={22} />
            ) : (
              <Switch
                checked={isOpen}
                onChange={(_, checked) => handleToggleClick(checked)}
                disabled={isDisabled}
                color="success"
                size="small"
              />
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              size="small"
              variant="outlined"
              startIcon={<PreBookIcon sx={{ fontSize: '0.85rem' }} />}
              disabled={!hasHybridLessons}
              onClick={() => navigate(`/${schoolSlug}/admin/hybrid-pre-booking`)}
              sx={{ fontSize: '0.7rem' }}
            >
              Pre-Book
            </Button>
          </Box>

          {/* Booking stats chips — own row, only shown when open */}
          {isOpen && (bookingChipStats || statsLoading) && (
            <Box sx={{ pl: '51px', mt: 0.75, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              <Chip
                label={statsLoading ? 'Loading…' : `${bookingChipStats!.fullyBooked} fully booked`}
                size="small"
                clickable={!statsLoading}
                onClick={!statsLoading ? () => setStatsModalOpen(true) : undefined}
                sx={{
                  bgcolor: 'rgba(150, 218, 201, 0.2)',
                  color: '#3a8f73',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  border: '1px solid #96DAC9',
                  cursor: statsLoading ? 'default' : 'pointer',
                  '&:hover': statsLoading ? {} : { bgcolor: 'rgba(150, 218, 201, 0.35)' },
                }}
              />
              <Chip
                label={statsLoading ? '' : `${bookingChipStats!.partiallyBooked} partial`}
                size="small"
                clickable={!statsLoading}
                onClick={!statsLoading ? () => setStatsModalOpen(true) : undefined}
                sx={{
                  bgcolor: 'rgba(255, 174, 158, 0.2)',
                  color: '#c0614e',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  border: '1px solid #FFAE9E',
                  cursor: statsLoading ? 'default' : 'pointer',
                  display: statsLoading ? 'none' : undefined,
                  '&:hover': statsLoading ? {} : { bgcolor: 'rgba(255, 174, 158, 0.35)' },
                }}
              />
              <Chip
                label={statsLoading ? '' : `${bookingChipStats!.notificationsSent} notified`}
                size="small"
                sx={{
                  bgcolor: 'rgba(69, 128, 228, 0.1)',
                  color: '#3060b0',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  border: '1px solid rgba(69, 128, 228, 0.4)',
                  display: statsLoading ? 'none' : undefined,
                }}
              />
            </Box>
          )}

          {/* Overflow toggle — own row below the stat chips. Lets parents book a
              clinic into the following week (N+1). Dependent on bookings being open. */}
          {hasHybridLessons && (
            <Box sx={{ pl: '51px', mt: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: isOpen ? 'text.primary' : 'text.secondary' }}
              >
                Allow next-week overflow
              </Typography>
              {overflowToggle.isPending ? (
                <CircularProgress size={18} />
              ) : (
                <Switch
                  checked={isOverflowOpen}
                  onChange={(_, checked) => handleOverflowToggle(checked)}
                  disabled={!isOpen}
                  color="success"
                  size="small"
                />
              )}
              <Tooltip title="Lets parents book a 1:1 clinic into the following week when the clinic week fills up. Requires bookings to be open.">
                <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
              </Tooltip>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={confirmOpen} onClose={handleCancel} maxWidth="xs" fullWidth>
        <DialogTitle>
          {pendingAction ? 'Open Hybrid Bookings?' : 'Close Hybrid Bookings?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingAction
              ? `This will open individual session bookings for all ${status?.totalHybridLessons} hybrid lesson${status?.totalHybridLessons !== 1 ? 's' : ''} in ${status?.termName || 'the current term'}. All enrolled parents will be notified by email to book their individual sessions.`
              : `This will close individual session bookings for all ${status?.totalHybridLessons} hybrid lesson${status?.totalHybridLessons !== 1 ? 's' : ''} in ${status?.termName || 'the current term'}. Parents will no longer be able to book or modify individual sessions.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color={pendingAction ? 'success' : 'warning'}
          >
            {pendingAction ? 'Open Bookings' : 'Close Bookings'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Booking Open Stats Modal */}
      <Dialog
        open={statsModalOpen}
        onClose={() => setStatsModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
            bgcolor: '#FCF6E6',
            borderBottom: '2px solid #e0e0e0',
          }}
        >
          <Box>
            <Typography variant="h6" component="span" fontWeight={700} color="text.primary">
              Booking Status
            </Typography>
            {termStats && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {termStats.totalBookingsMade} of {termStats.totalEmailsSent} parent{termStats.totalEmailsSent !== 1 ? 's' : ''} have booked
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={() => setStatsModalOpen(false)} aria-label="Close booking status dialog">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 2, bgcolor: '#FCF6E6' }}>
          {/* Filter bar: tabs + class dropdown */}
          <Box sx={{ mb: 2, mt: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              value={filterTab}
              exclusive
              onChange={(_, val) => { if (val) setFilterTab(val as FilterTab); }}
              size="small"
              aria-label="Filter bookings"
              sx={{
                '& .MuiToggleButton-root': {
                  borderColor: '#96DAC9',
                  color: '#9DA5AF',
                  fontSize: '0.75rem',
                  '&.Mui-selected': {
                    bgcolor: '#96DAC9',
                    color: '#080808',
                    fontWeight: 600,
                    '&:hover': { bgcolor: '#7fcabb' },
                  },
                  '&:hover': { bgcolor: 'rgba(150, 218, 201, 0.15)' },
                },
              }}
            >
              {(() => {
                const base = filterLessonId === 'all' ? allRecipients : allRecipients.filter((r) => r.lessonId === filterLessonId);
                return <>
                  <ToggleButton value="all" aria-label="Show all">
                    All ({base.length})
                  </ToggleButton>
                  <ToggleButton value="booked" aria-label="Show fully booked">
                    Fully booked ({base.filter((r) => r.bookedWeeks >= r.totalWeeks && r.totalWeeks > 0).length})
                  </ToggleButton>
                  <ToggleButton value="partial" aria-label="Show partially booked">
                    Partial ({base.filter((r) => r.bookedWeeks > 0 && r.bookedWeeks < r.totalWeeks).length})
                  </ToggleButton>
                  <ToggleButton value="pending" aria-label="Show not yet booked">
                    Not yet booked ({base.filter(isPendingRecipient).length})
                  </ToggleButton>
                </>;
              })()}
            </ToggleButtonGroup>

            {multipleLessons && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="class-filter-label" sx={{ fontSize: '0.8rem' }}>Class</InputLabel>
                <Select
                  labelId="class-filter-label"
                  value={filterLessonId}
                  label="Class"
                  onChange={(e) => setFilterLessonId(e.target.value)}
                  sx={{
                    fontSize: '0.8rem',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#96DAC9' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#3a8f73' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#3a8f73' },
                  }}
                >
                  <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>All classes</MenuItem>
                  {termStats?.lessons.map((lesson) => (
                    <MenuItem key={lesson.lessonId} value={lesson.lessonId} sx={{ fontSize: '0.8rem' }}>
                      {lesson.lessonName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          {statsLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress size={32} sx={{ color: '#4580E4' }} />
            </Box>
          ) : filteredRecipients.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              {filterTab === 'all' ? 'No enrolled parents found.' : 'No results for this filter.'}
            </Typography>
          ) : (
            <>
              {multipleLessons ? (
                termStats!.lessons.map((lesson) => {
                  const lessonRows = filteredRecipients.filter((r) => r.lessonName === lesson.lessonName);
                  if (lessonRows.length === 0) return null;
                  return (
                    <Box key={lesson.lessonId} sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#4580E4' }}>
                        {lesson.lessonName}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {lesson.bookingsMadeCount} / {lesson.emailsSentCount} booked
                        </Typography>
                      </Typography>
                      <LessonDetailBar lesson={lesson} />
                      <RecipientTable
                        rows={lessonRows}
                        individualWeeks={lesson.individualWeeks ?? []}
                        nudgeableWeeks={nudgeableClinicWeeks(lesson)}
                        onRowClick={handleRowClick}
                        onBookingClick={(entry, row) => setSelectedBooking({ entry, row })}
                        onNudgeClick={handleNudgeClick}
                        nudgingKey={nudgingKey}
                      />
                    </Box>
                  );
                })
              ) : (
                <>
                  {termStats?.lessons[0] && <LessonDetailBar lesson={termStats.lessons[0]} />}
                  <RecipientTable
                    rows={filteredRecipients}
                    individualWeeks={termStats?.lessons[0]?.individualWeeks ?? []}
                    nudgeableWeeks={termStats?.lessons[0] ? nudgeableClinicWeeks(termStats.lessons[0]) : []}
                    onRowClick={handleRowClick}
                    onBookingClick={(entry, row) => setSelectedBooking({ entry, row })}
                    onNudgeClick={handleNudgeClick}
                    nudgingKey={nudgingKey}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ bgcolor: '#FCF6E6', borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={() => setStatsModalOpen(false)}
            variant="outlined"
            sx={{ borderColor: '#4580E4', color: '#4580E4', '&:hover': { bgcolor: 'rgba(69,128,228,0.06)' } }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contact details popover */}
      <Popover
        open={!!contactAnchor}
        anchorEl={contactAnchor}
        onClose={handleContactClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            bgcolor: '#FCF6E6',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            width: 300,
            p: 2,
          },
        }}
      >
        {contactRow && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                {contactRow.parentName}
              </Typography>
              <IconButton size="small" onClick={handleContactClose}>
                <CloseIcon sx={{ fontSize: '0.9rem' }} />
              </IconButton>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Parent of {contactRow.studentName}
            </Typography>
            <Divider sx={{ mb: 1.5, borderColor: '#e0e0e0' }} />

            {/* In-platform message */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
              <EmailIcon sx={{ fontSize: '0.85rem' }} /> Send a message
            </Typography>
            {messageSent ? (
              <Alert severity="success" sx={{ fontSize: '0.78rem', py: 0.5 }}>
                Message sent — they'll see it when they log in.
              </Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                  placeholder={`Message to ${contactRow.parentName}…`}
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  inputProps={{ maxLength: 2000 }}
                  sx={{ fontSize: '0.82rem' }}
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled={!messageText.trim() || sendMessage.isPending}
                  onClick={() => sendMessage.mutate(messageText.trim())}
                  sx={{ alignSelf: 'flex-end', fontSize: '0.75rem' }}
                >
                  {sendMessage.isPending ? <CircularProgress size={14} color="inherit" /> : 'Send'}
                </Button>
                {sendMessage.isError && (
                  <Alert severity="error" sx={{ fontSize: '0.75rem', py: 0.25 }}>Failed to send. Please try again.</Alert>
                )}
              </Box>
            )}

            {/* Phone */}
            {contactRow.parentPhone && (
              <>
                <Divider sx={{ my: 1.5, borderColor: '#e0e0e0' }} />
                <Button
                  component="a"
                  href={`tel:${contactRow.parentPhone}`}
                  startIcon={<PhoneIcon sx={{ fontSize: '1rem' }} />}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderColor: '#96DAC9',
                    color: '#3a8f73',
                    justifyContent: 'flex-start',
                    fontSize: '0.78rem',
                    textTransform: 'none',
                    width: '100%',
                    '&:hover': { bgcolor: 'rgba(150,218,201,0.1)' },
                  }}
                >
                  {contactRow.parentPhone}
                </Button>
              </>
            )}
          </Box>
        )}
      </Popover>

      {/* Booking detail dialog */}
      <BookingDetailDialog
        entry={selectedBooking?.entry ?? null}
        studentName={selectedBooking?.row.studentName ?? ''}
        lessonName={selectedBooking?.row.lessonName ?? ''}
        lessonId={selectedBooking?.row.lessonId ?? ''}
        onClose={() => setSelectedBooking(null)}
        onMutated={handleBookingMutated}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Lesson detail bar — shows teacher, instrument, room, day/time
// ---------------------------------------------------------------------------

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function LessonDetailBar({ lesson }: { lesson: TermBookingOpenStatsLesson }) {
  const dayTime = [
    lesson.dayOfWeek != null ? DAY_NAMES[lesson.dayOfWeek] : null,
    lesson.startTime && lesson.endTime ? `${lesson.startTime} – ${lesson.endTime}` : lesson.startTime,
  ].filter(Boolean).join(' • ');

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5, mb: 1.5 }}>
      {lesson.teacherName && (
        <Chip
          label={lesson.teacherName}
          size="small"
          sx={{
            fontSize: '0.7rem',
            height: 20,
            bgcolor: lesson.teacherColor ?? '#4580E4',
            color: '#fff',
          }}
        />
      )}
      {lesson.instrumentName && (
        <Chip
          label={lesson.instrumentName}
          size="small"
          sx={{
            fontSize: '0.7rem',
            height: 20,
            ...(lesson.instrumentColor
              ? { bgcolor: lesson.instrumentColor, color: getContrastTextColor(lesson.instrumentColor), border: 'none' }
              : { variant: 'outlined' }),
          }}
        />
      )}
      {lesson.roomName && (
        <Chip
          label={lesson.locationName ? `${lesson.roomName} · ${lesson.locationName}` : lesson.roomName}
          size="small"
          variant="outlined"
          sx={{ fontSize: '0.7rem', height: 20 }}
        />
      )}
      {dayTime && (
        <Chip label={dayTime} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Booking detail dialog — edit / cancel an individual booking
// ---------------------------------------------------------------------------

interface BookingDetailDialogProps {
  entry: BookingOpenStatsWeekEntry | null;
  studentName: string;
  lessonName: string;
  lessonId: string;
  onClose: () => void;
  onMutated: () => void;
}

function BookingDetailDialog({ entry, studentName, lessonName, lessonId, onClose, onMutated }: BookingDetailDialogProps) {
  const [editMode, setEditMode] = useState(false);
  const [cancelMode, setCancelMode] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<import('../../api/hybridBooking.api').TimeSlot | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  // Per-booking Change Teacher (2026-09-06).
  const [teacherMode, setTeacherMode] = useState(false);

  const { data: availableSlots, isLoading: slotsLoading } = useAdminAvailableSlots(
    editMode && entry ? lessonId : '',
    editMode && entry ? entry.weekNumber : 0,
    editMode && entry ? entry.bookingId : undefined,
  );

  const reschedule = useAdminRescheduleBooking();
  const cancel = useAdminCancelBooking();

  useEffect(() => {
    setEditMode(false);
    setCancelMode(false);
    setSelectedSlot(null);
    setCancelReason('');
    setTeacherMode(false);
  }, [entry?.bookingId]);

  const handleClose = () => {
    setEditMode(false);
    setCancelMode(false);
    setSelectedSlot(null);
    setCancelReason('');
    onClose();
  };

  const handleConfirmReschedule = () => {
    if (!entry || !selectedSlot) return;
    const dateKey = selectedSlot.dateKey ?? (selectedSlot.date ? new Date(selectedSlot.date).toISOString().split('T')[0] : '');
    reschedule.mutate(
      { id: entry.bookingId, data: { scheduledDate: `${dateKey}T00:00:00.000Z`, startTime: selectedSlot.startTime, endTime: selectedSlot.endTime } },
      { onSuccess: () => { handleClose(); onMutated(); } },
    );
  };

  const handleConfirmCancel = () => {
    if (!entry) return;
    cancel.mutate(
      { id: entry.bookingId, reason: cancelReason || undefined },
      { onSuccess: () => { handleClose(); onMutated(); } },
    );
  };

  if (!entry) return null;

  const scheduledDate = parseUTCDate(entry.scheduledDate);

  // Find the current slot to highlight it in the slot picker
  const currentSlot = availableSlots?.find(
    (s) => s.startTime === entry.startTime && s.dateKey === entry.scheduledDate.split('T')[0]
  ) ?? null;

  return (
    <Dialog open={!!entry} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" component="span">1:1 Clinic — {studentName}</Typography>
          <Typography variant="body2" color="text.secondary">Week {entry.weekNumber} · {lessonName}</Typography>
        </Box>
        <IconButton size="small" onClick={handleClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Date</Typography>
            <Typography variant="body1">{format(scheduledDate, 'EEE, d MMM yyyy')}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Time</Typography>
            <Typography variant="body1">{entry.startTime} – {entry.endTime}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Week</Typography>
            <Typography variant="body1">Week {entry.weekNumber}</Typography>
          </Grid>
          {entry.teacherName && (
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Teacher</Typography>
              <Typography variant="body1">
                {entry.teacherName}
                {entry.overrideTeacherId ? ' (covering)' : ''}
              </Typography>
            </Grid>
          )}

          {editMode && (
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Reschedule to a new slot in week {entry.weekNumber}</Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Admin reschedule bypasses the 24-hour notice and bookings-open checks. Slot must be in the same week.
              </Alert>
              <SlotPicker
                slots={availableSlots}
                selectedSlot={selectedSlot}
                onSlotSelect={setSelectedSlot}
                isLoading={slotsLoading}
                emptyMessage="No other available slots for this week."
                confirmationPrefix="New time:"
                currentSlot={currentSlot}
              />
            </Grid>
          )}

          {cancelMode && (
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }} />
              <Alert severity="warning" sx={{ mb: 2 }}>Cancel this booking? The slot will become available again.</Alert>
              <TextField
                label="Cancellation reason (optional)"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                fullWidth multiline rows={2}
                inputProps={{ maxLength: 500 }}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        {!editMode && !cancelMode && (
          <>
            <Button onClick={handleClose}>Close</Button>
            <Button color="error" startIcon={<CancelBookingIcon />} onClick={() => setCancelMode(true)}>
              Cancel Booking
            </Button>
            <Button variant="outlined" startIcon={<SwapHorizIcon />} onClick={() => setTeacherMode(true)}>
              Change Teacher
            </Button>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditMode(true)}>
              Edit Booking
            </Button>
          </>
        )}
        {editMode && (
          <>
            <Button onClick={() => { setEditMode(false); setSelectedSlot(null); }}>Back</Button>
            <Button variant="contained" onClick={handleConfirmReschedule} disabled={!selectedSlot || reschedule.isPending}>
              {reschedule.isPending ? <CircularProgress size={20} /> : 'Confirm Reschedule'}
            </Button>
          </>
        )}
        {cancelMode && (
          <>
            <Button onClick={() => { setCancelMode(false); setCancelReason(''); }}>Back</Button>
            <Button variant="contained" color="error" onClick={handleConfirmCancel} disabled={cancel.isPending}>
              {cancel.isPending ? <CircularProgress size={20} /> : 'Confirm Cancel'}
            </Button>
          </>
        )}
      </DialogActions>
      {teacherMode && (
      <ChangeClinicTeacherDialog
        open
        onClose={() => setTeacherMode(false)}
        bookingId={entry.bookingId}
        lessonId={lessonId}
        currentTeacherId={entry.overrideTeacherId ?? undefined}
        currentTeacherName={entry.teacherName}
        weekNumber={entry.clinicWeekNumber ?? entry.weekNumber}
        scheduledDate={entry.scheduledDate}
        startTime={entry.startTime}
        endTime={entry.endTime}
        onChanged={() => { handleClose(); onMutated(); }}
      />
      )}
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Extracted table sub-component to keep JSX readable
// ---------------------------------------------------------------------------

type RecipientRow = BookingOpenStatsRecipient & { lessonName: string; lessonId: string };

/** Visually hidden, still read by screen readers (the standard sr-only rule). */
const SR_ONLY = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

/**
 * The clinic weeks that apply to a student: those on or after their effective start week
 * (mid-term start). A missing `startWeek` (stale backend) means week 1 — nothing is excluded.
 */
function applicableClinicWeeks(individualWeeks: number[], startWeek?: number): number[] {
  return individualWeeks.filter((wk) => wk >= (startWeek ?? 1));
}

/**
 * Bookings that count toward a student's total: those FOR a clinic week on or after their
 * effective start (keyed on the clinic, `clinicWeekNumber ?? weekNumber`, so an overflow
 * booking held in N+1 still counts for clinic N). A missing `startWeek` means week 1.
 */
function applicableBookingCount(
  weekBookings: Array<{ weekNumber: number; clinicWeekNumber?: number | null }>,
  startWeek?: number
): number {
  return weekBookings.filter((b) => (b.clinicWeekNumber ?? b.weekNumber) >= (startWeek ?? 1)).length;
}

/**
 * Nothing booked for any week that applies — a pre-start booking does not count — but a
 * student none of whose clinic weeks has started yet is not "unbooked".
 */
function isPendingRecipient(r: { bookedWeeks: number; allWeeksNotApplicable?: boolean }): boolean {
  return r.bookedWeeks === 0 && !r.allWeeksNotApplicable;
}

export interface RecipientTableProps {
  rows: RecipientRow[];
  individualWeeks: number[];
  onRowClick: (row: RecipientRow, anchor: HTMLElement) => void;
  onBookingClick: (entry: BookingOpenStatsWeekEntry, row: RecipientRow) => void;
  /**
   * Sprint B: the admin "nudge now". ⚠️ Nudges EVERY unbooked family in that
   * class for that week — the endpoint is per (lesson, week), not per row — and
   * the tooltip says so. The chip is the affordance because the "Not yet
   * booked" tab hides a partially-booked student missing exactly one week.
   */
  onNudgeClick?: (row: RecipientRow, weekNumber: number) => void;
  /**
   * `${lessonId}#${weekNumber}` of the MOST RECENT press in flight. The key is
   * per class + week, so every unbooked chip in that column spins together —
   * which matches what the press does.
   */
  nudgingKey?: string | null;
  /**
   * Clinic weeks whose `—` cells get a LIVE nudge chip. Omit = every week
   * (the parent computes it with `nudgeableClinicWeeks`). A week not listed
   * renders a plain dash — the server would refuse it with a 409 anyway.
   */
  nudgeableWeeks?: number[];
}

export function RecipientTable({ rows, individualWeeks, onRowClick, onBookingClick, onNudgeClick, nudgingKey, nudgeableWeeks }: RecipientTableProps) {
  const weeks = individualWeeks.length > 0
    ? [...individualWeeks].sort((a, b) => a - b)
    : [];

  const parentColWidth = weeks.length > 0 ? '28%' : '35%';
  const studentColWidth = weeks.length > 0 ? '22%' : '35%';
  const weekColWidth = weeks.length > 0 ? `${Math.floor(40 / Math.max(weeks.length, 1))}%` : '0%';

  return (
    <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
      <colgroup>
        <col style={{ width: parentColWidth }} />
        <col style={{ width: studentColWidth }} />
        {weeks.map((wk) => <col key={wk} style={{ width: weekColWidth }} />)}
      </colgroup>
      <TableHead>
        <TableRow sx={{ bgcolor: '#FCF6E6' }}>
          <TableCell sx={{ fontWeight: 700, color: '#080808', borderBottom: '2px solid #96DAC9' }}>Parent</TableCell>
          <TableCell sx={{ fontWeight: 700, color: '#080808', borderBottom: '2px solid #96DAC9' }}>Student</TableCell>
          {weeks.map((wk) => (
            <TableCell key={wk} align="center" sx={{ fontWeight: 700, color: '#080808', borderBottom: '2px solid #96DAC9' }}>
              Wk {wk}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow
            key={`${row.parentId}-${row.studentId}`}
            hover
            onClick={(e) => onRowClick(row, e.currentTarget)}
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'rgba(150, 218, 201, 0.08)' } }}
          >
            <TableCell sx={{ color: '#080808' }}>{row.parentName}</TableCell>
            <TableCell sx={{ color: '#080808' }}>{row.studentName}</TableCell>
            {weeks.map((wk) => {
              // Match by clinic week so an overflow booking (held in week N+1)
              // shows in its clinic's column N.
              const entry = row.weekBookings?.find((b) => b.clinicWeekNumber === wk);
              // Before the student's start week the clinic does not apply to them: a muted
              // dash, never the unbooked state and never a nudge target.
              if (!entry && wk < (row.startWeek ?? 1)) {
                return (
                  <TableCell key={wk} align="center">
                    <Typography
                      variant="body2"
                      component="span"
                      color="text.disabled"
                      title={`Starts Week ${row.startWeek}`}
                      data-testid={`not-applicable-${wk}`}
                    >
                      {/* The dash is decoration; the reason is real text a screen reader reads
                          (an aria-label on a bare span is not reliably announced). */}
                      <span aria-hidden="true">–</span>
                      <Box component="span" sx={SR_ONLY}>
                        {`Starts Week ${row.startWeek}`}
                      </Box>
                    </Typography>
                  </TableCell>
                );
              }
              if (!entry) {
                const nudging = nudgingKey === `${row.lessonId}#${wk}`;
                // A past, too-soon, open or out-of-term week gets a plain dash.
                const canNudge = Boolean(onNudgeClick) && (nudgeableWeeks ? nudgeableWeeks.includes(wk) : true);
                const chip = (
                  <Chip
                    label="—"
                    size="small"
                    // ⚠️ `clickable` stays TRUE while in flight. MUI picks the host
                    // element from `clickable`, not `disabled`: turning it off made
                    // the busy chip a bare <div>, so the disabled attribute, the
                    // button role and the aria-label all vanished from the
                    // accessibility tree mid-press (Phase 5 /qa, HIGH). Keeping it a
                    // ButtonBase and letting `disabled` do the work keeps focus,
                    // the role and the announcement intact.
                    clickable={canNudge}
                    disabled={nudging}
                    aria-busy={nudging || undefined}
                    icon={
                      canNudge
                        ? nudging
                          ? <CircularProgress size={12} thickness={5} sx={{ color: '#080808' }} />
                          : <NudgeIcon />
                        : undefined
                    }
                    aria-label={
                      canNudge
                        ? `Nudge unbooked families to book week ${wk}${nudging ? ' — sending' : ''}`
                        : undefined
                    }
                    onClick={
                      canNudge && !nudging
                        ? (e) => { e.stopPropagation(); onNudgeClick!(row, wk); }
                        : undefined
                    }
                    sx={{
                      bgcolor: 'rgba(255, 174, 158, 0.3)',
                      color: '#080808',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      '& .MuiChip-icon': { fontSize: '0.9rem', color: 'inherit' },
                    }}
                  />
                );
                return (
                  <TableCell key={wk} align="center">
                    {canNudge ? (
                      <Tooltip title={`Email everyone in ${row.lessonName ?? 'this class'} who hasn't booked week ${wk}. Families already nudged are skipped.`}>
                        <span>{chip}</span>
                      </Tooltip>
                    ) : chip}
                  </TableCell>
                );
              }
              const dateStr = formatUTCDate(entry.scheduledDate, { day: '2-digit', month: '2-digit' }, 'en-GB');
              const isOverflow = entry.weekNumber !== entry.clinicWeekNumber;
              return (
                <TableCell
                  key={wk}
                  align="center"
                  onClick={(e) => { e.stopPropagation(); onBookingClick(entry, row); }}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'rgba(69, 128, 228, 0.1)' },
                  }}
                >
                  <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2, color: '#4580E4' }}>{entry.startTime}</Typography>
                  <Typography variant="caption" color="text.secondary">{dateStr}</Typography>
                  {isOverflow && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', lineHeight: 1.1, color: '#c0614e', fontWeight: 600 }}
                    >
                      Wk{entry.weekNumber} (overflow)
                    </Typography>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
